import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { VisualImpairmentsPanelComponent } from '../visual-impairments-panel/visual-impairments-panel.component';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { ThemeService } from '@services/theme.service';
import {IconPathPipe} from "@app/utils/pipes/icon-path-pipe";
import {SelectLanguageComponent} from "@components/select-language/select-language.component";
import {LogoComponent} from '@components/logo/logo.component';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import { isAppZoomUserOverride, syncResponsiveAppZoom } from '@app/utils/app-zoom.util';

@Component({
   selector: 'main-header',
   standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToolbarModule,
    ButtonModule,
    AvatarModule,
    MenuModule,
    TooltipModule,
    TranslateModule,
    ConfirmDialogModule,
    VisualImpairmentsPanelComponent,
    IconPathPipe,
    SelectLanguageComponent,
    LogoComponent,
  ],
   providers: [ConfirmationService],
   templateUrl: './main-header.component.html',
   styleUrl: './main-header.component.scss',
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainHeaderComponent implements OnInit, OnDestroy {
   notificationCount = 0;
   userInitials = 'JD';
   showAccessibilityPanel = false;
   private resizeTimer: ReturnType<typeof setTimeout> | null = null;

   languages = [
      { label: "O'zbek", value: 'uz-Latn-UZ', flag: 'uz-latn.png' },
      { label: 'English', value: 'en-US', flag: 'en-us.png' },
      { label: 'Русский', value: 'ru-RU', flag: 'ru-ru.png' }
   ];

   selectedLanguage = this.languages[0];

   constructor(
      private translate: TranslateService,
      private cdr: ChangeDetectorRef,
      private router: Router,
      private authService: AuthService,
      public themeService: ThemeService,
      private confirmationService: ConfirmationService,
      private regionManagerService: RegionManagerService
   ) {}

   ngOnInit() {
      syncResponsiveAppZoom();

      const username = localStorage.getItem('username') ;
        if (username) {
             this.userInitials = username.split(' ').map(name => name.charAt(0).toUpperCase()).join('');
        }

      const currentLang = this.translate.currentLang || this.translate.defaultLang;
      const langItem = this.languages.find(lang => lang.value === currentLang);
      if (langItem) {
         this.selectedLanguage = langItem;
      }

      this.updateProfileMenuItems();
      this.updateHelpMenuItems();
      this.translate.onLangChange.subscribe(() => {
         this.updateProfileMenuItems();
         this.updateHelpMenuItems();
      });
   }

   ngOnDestroy(): void {
      if (this.resizeTimer) {
         clearTimeout(this.resizeTimer);
      }
   }

   @HostListener('window:resize')
   onWindowResize(): void {
      if (isAppZoomUserOverride()) {
         return;
      }
      if (this.resizeTimer) {
         clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
         syncResponsiveAppZoom();
      }, 150);
   }

   profileMenuItems: MenuItem[] = [];
   helpMenuItems: MenuItem[] = [];

   toggleTheme() {
      this.themeService.toggleTheme();
      this.cdr.detectChanges();
   }

   selectLanguage(language: any) {
      this.selectedLanguage = language;
      this.translate.use(language.value);
      localStorage.setItem('lang', language.value);
   }

   getIconPath(iconName: string): string {
      const theme = this.themeService.isDarkMode() ? 'dark' : 'light';
      return `assets/icons/main/${theme}/ico-${iconName}.svg`;
   }

   updateProfileMenuItems() {
      this.profileMenuItems = [
         {
            label: this.translate.instant('Profile'),
            icon: this.getIconPath('account'),
            escape: false,
            command: () => {
               this.router.navigate(['/profile']);
            }
         },
         {
            label: this.translate.instant('Settings'),
            icon: this.getIconPath('settings'),
            escape: false,
            command: () => {
               this.router.navigate(['/settings']);
            }
         },
         {
            label: this.translate.instant('Logout'),
            icon: this.getIconPath('out-link'),
            escape: false,
            command: () => {
               this.logout();
            }
         }
      ];
   }

   updateHelpMenuItems() {
      this.helpMenuItems = [
         {
            label: this.translate.instant('Electronic Documentation'),
            command: () => {
               this.router.navigate(['/help/documentation']);
            }
         },
         {
            label: this.translate.instant('Support Tickets'),
            command: () => {
               this.router.navigate(['/help/support-tickets']);
            }
         }
      ];
   }

   toggleAccessibilityPanel() {
      this.showAccessibilityPanel = !this.showAccessibilityPanel;
   }

   onAccessibilityPanelClose() {
      this.showAccessibilityPanel = false;
   }

   logout() {
      this.confirmationService.confirm({
         message: this.translate.instant('LogoutConfirmMessage'),
         header: this.translate.instant('LogoutConfirmTitle'),
         icon: 'pi pi-exclamation-triangle',
         acceptLabel: this.translate.instant('Yes'),
         rejectLabel: this.translate.instant('No'),
         accept: async () => {
            await this.regionManagerService.clearRegionCacheOnLogout();
            this.authService.logout();
            await this.router.navigate(['/login']);
         },
         reject: () => {
         }
      });
   }



}
